import {
  ArchClient,
  ArchCourse,
  ArchModule,
} from 'apis/entities/architecture.entity'
import { IconClass } from 'components/Svg'

// ArchClient structure: client -> program -> intake -> course -> module -> topic
//
// UNSW Online uses a different conceptual model from all other ("enterprise") clients.
// See README.md § "Architecture Tool: UNSWO vs Enterprise Mode" for the full explanation.
// Use isUnswOnlineClient() everywhere client identity drives branching — never match the
// string 'UNSW Online' directly, so a rename can never silently break the logic.
export const UNSW_ONLINE_CLIENT_NAME = 'UNSW Online'
export function isUnswOnlineClient(client: { name: string }): boolean {
  return client.name === UNSW_ONLINE_CLIENT_NAME
}

export enum ArchActionType {
  Client = 'client',
  Program = 'program',
  Intake = 'intake',
  Course = 'course',
  Module = 'module',
  Topic = 'topic',
  CourseModules = 'course-modules',
  CourseAssessments = 'course-assessments',
  CourseAssessmentsTests = 'course-assessments-tests',
  Quiz = 'quiz',
}

export const getIntakeCountByProgramId = (
  client: ArchClient,
  programId: string,
): number => {
  const program = client.programs.find((program) => program.id === programId)
  return program?.intakes.length || 0
}

export const getCourseCountByIntakeId = (
  client: ArchClient,
  intakeId: string,
): number => {
  // find the intake in the client's programs
  const intake = client.programs
    .flatMap((program) => program.intakes)
    .find((intake) => intake.id === intakeId)
  return intake?.courses.length || 0
}

export const getModuleCountByCourseId = (
  client: ArchClient,
  courseId: string,
): number => {
  // find the course in the client's programs
  const course = client.programs
    .flatMap((program) => program.intakes)
    .flatMap((intake) => intake.courses)
    .find((course) => course.id === courseId)
  return course?.modules.length || 0
}

export const getTopicCountByModuleId = (
  client: ArchClient,
  moduleId: string,
): number => {
  // find the module in the client's programs
  const module = client.programs
    .flatMap((program) => program.intakes)
    .flatMap((intake) => intake.courses)
    .flatMap((course) => course.modules)
    .find((module) => module.id === moduleId)
  return module?.topics.length || 0
}

/**
 * sample 1: Are you sure you want to delete these 2 modules, including 6 topics?
 * sample 2: Are you sure you want to delete these 2 modules, including 1 topic?
 * sample 3: Are you sure you want to delete these 4 topics?
 * sample 4: Are you sure you want to delete these 6 topics?
 * sample 5: Are you sure you want to delete this course?
 * sample 6: Are you sure you want to delete this topic?
 * sample 7: Are you sure you want to delete these 2 courses, including 12 modules?
 * sample 8: Are you sure you want to delete this course, including 6 modules?
 * @returns delete confirmation text
 */
// if single parent use `this`, if more than one parent use `these`
// if single child use single form, if more than one child use plural form
export const getDeleteConfirmationMessage = (
  parent: string,
  parentCount: number,
  child: string,
  childCount: number,
): string => {
  if (parentCount === 1) {
    if (childCount === 1) {
      return `Are you sure you want to delete this ${parent}, including ${childCount} ${child}?`
    } else if (child) {
      return `Are you sure you want to delete this ${parent}, including ${childCount} ${child}s?`
    } else {
      return `Are you sure you want to delete this ${parent}?`
    }
  } else if (child) {
    if (childCount === 1) {
      return `Are you sure you want to delete these ${parentCount} ${parent}s, including ${childCount} ${child}?`
    } else {
      return `Are you sure you want to delete these ${parentCount} ${parent}s, including ${childCount} ${child}s?`
    }
  } else {
    return `Are you sure you want to delete these ${parentCount} ${parent}s?`
  }
}

export const getModuleByTopicId = (
  client: ArchClient,
  topicId: string,
): ArchModule | null => {
  let selectedModule: ArchModule | null = null
  client.programs.forEach((program) => {
    program.intakes.forEach((intake) => {
      intake.courses.forEach((course) => {
        course.modules.forEach((module) => {
          module.topics.forEach((topic) => {
            if (topic.id === topicId) {
              selectedModule = module
            }
          })
        })
      })
    })
  })
  return selectedModule
}

export const getCourseByModuleId = (
  client: ArchClient,
  moduleId: string,
): ArchCourse | null => {
  let selectedCourse: ArchCourse | null = null
  client.programs.forEach((program) => {
    program.intakes.forEach((intake) => {
      intake.courses.forEach((course) => {
        course.modules.forEach((module) => {
          if (module.id === moduleId) {
            selectedCourse = course
          }
        })
      })
    })
  })
  return selectedCourse
}

// assume quiz ids are unique across courses
export const getCourseByQuizId = (
  client: ArchClient,
  quizId: string,
): ArchCourse | null => {
  let selectedCourse: ArchCourse | null = null
  client.programs.forEach((program) => {
    program.intakes.forEach((intake) => {
      intake.courses.forEach((course) => {
        if (course.quizzes) {
          course.quizzes.forEach((quiz) => {
            if (quiz.id === quizId) {
              selectedCourse = course
            }
          })
        }
      })
    })
  })
  return selectedCourse
}

export const getExportFileNameForCourse = (courseName: string): string => {
  // remove special characters and replace with hyphen
  return `${courseName.replace(/[^a-zA-Z0-9]/g, '-')}.xlsx`
}

export const convertIntakeNameToDate = (intakeName: string): Date | null => {
  // possible intake name format: 'Jan 2024', 'Feb-2024', 'March 2024'
  // if intake name is not in the expected format, return null
  let intakeNameParts = intakeName.split(' ').filter((part) => part.trim())
  if (intakeNameParts.length !== 2) {
    intakeNameParts = intakeName.split('-').filter((part) => part.trim())
  }
  // console.log(`intakeNameParts: ${intakeNameParts}, intakeName: ${intakeName}`)
  if (intakeNameParts.length !== 2) {
    return null
  }

  const month = intakeNameParts[0]
  const year = intakeNameParts[1]

  // validate year
  if (isNaN(parseInt(year))) {
    return null
  }

  const monthIndex = new Date(Date.parse(`1 ${month} 2020`)).getMonth()
  if (isNaN(monthIndex)) {
    return null
  }
  const date = new Date(Date.parse(`${year},${monthIndex + 1},1`))
  // console.log(`intakeName: ${intakeName}, date: ${date}`)
  return date
}

export const sortIntakeOptionsByCreatedAt = (intakeOptions: any[]): any[] => {
  // sort intake options by createdAt date descending
  return intakeOptions.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )
}

export const sortIntakeOptionsByDate = (intakeOptions: any[]): any[] => {
  // sort intake options by date first, otherwise by alphabet ascending
  // separate intake options into two arrays: one with date, one without date
  const intakeOptionsWithDate = intakeOptions.filter((intakeOption) =>
    convertIntakeNameToDate(intakeOption.name),
  )

  const intakeOptionsWithoutDate = intakeOptions.filter(
    (intakeOption) => !convertIntakeNameToDate(intakeOption.name),
  )

  // sort intake options with date by date
  const sortedIntakeOptionsWithDate = intakeOptionsWithDate.sort(
    (a, b) =>
      convertIntakeNameToDate(a.name)!.getTime() -
      convertIntakeNameToDate(b.name)!.getTime(),
  )

  // sort intake options without date by alphabet ascending
  const sortedIntakeOptionsWithoutDate = intakeOptionsWithoutDate.sort((a, b) =>
    a.name.localeCompare(b.name),
  )

  // combine two sorted arrays
  // console.log(sortedIntakeOptionsWithDate)
  // console.log(sortedIntakeOptionsWithoutDate)
  return sortedIntakeOptionsWithDate.concat(sortedIntakeOptionsWithoutDate)
}

// base: 12, each nesting level: +16, checkbox compensation: +18 + gap
export const nestingPadding = (depth: number, checkboxGap = 0): number =>
  12 + depth * 16 + (checkboxGap > 0 ? 18 + checkboxGap : 0)

export const getTooltipConfig = (
  type: ArchActionType,
  isUnswOnline = true,
): { id: string; displayName: string } => {
  if (type === ArchActionType.Client) {
    return { id: 'client', displayName: 'everything' }
  }
  if (type === ArchActionType.CourseModules) {
    return {
      id: 'course-modules',
      displayName: isUnswOnline ? 'course weeks' : 'course modules',
    }
  }
  if (type === ArchActionType.Module) {
    return { id: 'module', displayName: isUnswOnline ? 'week' : 'module' }
  }
  if (type === ArchActionType.CourseAssessments) {
    return { id: 'course-assessments', displayName: 'course assessments' }
  }
  if (type === ArchActionType.CourseAssessmentsTests) {
    return { id: 'course-assessments-tests', displayName: 'course tests' }
  }
  if (type === ArchActionType.Intake) {
    if (isUnswOnline) {
      return { id: 'cohort', displayName: 'hexamester' }
    } else {
      return { id: 'cohort', displayName: 'cohort' }
    }
  }
  // if (isUnswOnline && type === ArchActionType.Course) {
  //   return { id: 'course', displayName: 'course' }
  // }
  if (isUnswOnline && type === ArchActionType.Program) {
    return { id: 'program', displayName: 'course' }
  }
  return { id: type, displayName: type }
}

/**
 * Returns the capitalised display label for an entity type, respecting UNSW Online renaming.
 * e.g. Program → "Course", Intake → "Hexamester" in UNSW Online mode.
 */
export const getEntityLabel = (
  type: ArchActionType,
  isUnswOnline = true,
): string => {
  const { displayName } = getTooltipConfig(type, isUnswOnline)
  return displayName.charAt(0).toUpperCase() + displayName.slice(1)
}

export const getDraggingStyle = (isDragging: boolean) => ({
  boxShadow: isDragging ? '0px 16px 24px 0px rgba(13, 13, 13, 0.2)' : '',
  borderLeft: isDragging ? '' : '1px solid #D9D9D9C2',
  borderRight: isDragging ? '' : '1px solid #D9D9D9C2',
})

export const getIconClasses = (
  type: ArchActionType,
  isEnterprise: boolean,
): { stroke: IconClass; fill: IconClass } => {
  if (!isEnterprise) {
    return {
      stroke: IconClass.StrokeBlackSolid,
      fill: IconClass.FillBlackSolid,
    }
  }
  switch (type) {
    case ArchActionType.CourseModules:
    case ArchActionType.Module:
    case ArchActionType.Intake:
      return { stroke: IconClass.StrokeWhite, fill: IconClass.FillWhite }
    case ArchActionType.Client:
    case ArchActionType.CourseAssessments:
    case ArchActionType.CourseAssessmentsTests:
      return { stroke: IconClass.StrokeBlack, fill: IconClass.FillWhite }
    default:
      return { stroke: IconClass.StrokeBlack, fill: IconClass.Default }
  }
}

export const getArchBreadcrumb = (
  isEnterprise: boolean,
  selectedClient: { name: string } | null,
  selectedProgram: { name: string } | null,
  selectedIntake: { name: string } | null,
): string => {
  if (!isEnterprise) {
    const course = selectedProgram ? selectedProgram.name : 'Course'
    const hexamester = selectedIntake ? selectedIntake.name : 'Hexamester'
    return `${course}/${hexamester}`
  }
  const words = [
    selectedClient ? selectedClient.name : 'Client',
    selectedProgram ? selectedProgram.name : 'Program',
    selectedIntake ? selectedIntake.name : 'Cohort',
    'Course',
  ]
  return words.join('/')
}

// Course row collapse visibility helpers
// These functions are the single source of truth for what is shown/hidden
// when a course or its sub-sections are collapsed.

export const isCourseChildHeaderVisible = (
  collapsedCourseIds: string[],
  courseId: string,
): boolean => !collapsedCourseIds.includes(courseId)

export const isCourseModuleItemsVisible = (
  collapsedCourseIds: string[],
  collapsedCourseModulesIds: string[],
  courseId: string,
): boolean =>
  !collapsedCourseIds.includes(courseId) &&
  !collapsedCourseModulesIds.includes(courseId)

export const isCourseAssessmentItemsVisible = (
  collapsedCourseIds: string[],
  collapsedCourseAssessmentsIds: string[],
  courseId: string,
): boolean =>
  !collapsedCourseIds.includes(courseId) &&
  !collapsedCourseAssessmentsIds.includes(courseId)
